import dynamic from "next/dynamic";
import Link from "next/link";
import { useRouter } from "next/router";

import { useCallback, useEffect, useMemo, useState } from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";

import Bugsnag from "@bugsnag/js";
import classNames from "classnames";
import format from "date-fns/format";
import parseJSON from "date-fns/parseJSON";
import truncate from "lodash/truncate";
import unescape from "lodash/unescape";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { faCommentDots } from "@fortawesome/free-regular-svg-icons/faCommentDots";
import { faThumbsUp } from "@fortawesome/free-regular-svg-icons/faThumbsUp";
import { faLock } from "@fortawesome/free-solid-svg-icons/faLock";
import { faStar } from "@fortawesome/free-solid-svg-icons/faStar";
import { faSync } from "@fortawesome/free-solid-svg-icons/faSync";
import { faThumbsUp as faThumbsUpSolid } from "@fortawesome/free-solid-svg-icons/faThumbsUp";

import DiscussionSnippet from "components/DiscussionSnippet";
import FollowButton from "components/FollowButton";
import FontAwesomeIcon from "components/FontAwesomeIcon";
import ProfileLogo from "components/ProfileLogo";
import PublicationBadges from "components/PublicationBadges";
import PublicationSaveButton from "components/PublicationSaveButton";
import ShareButton from "components/ShareButton";
import { useApi } from "hooks/useApi";
import useAuthentication from "hooks/useAuthentication";
import { useGlobalState } from "hooks/useGlobalState";
import useIframeModal from "hooks/useIframeModal";
import { useInsightInteractions } from "hooks/useInsightInteractions";
import useMediaQuery from "hooks/useMediaQuery";
import useTheme from "hooks/useTheme";
import { baseFetcher, fetchApi } from "utils/baseFetcher";
import { emberAppBaseUrl } from "utils/envVariables";
import highlightText from "utils/highLightText";
import htmlText from "utils/htmlToText";
import objectToQueryString from "utils/objectToQueryString";
import { USE_EMBER_IFRAME } from "consts";

import type { MoreOptions } from "./MoreButton/types";
import InsightDiscussionsList from "./InsightDiscussionsList";
import styles from "./style.module.scss";

const InsightModal = dynamic(() => import("components/InsightModal"));
const MoreButton = dynamic(() => import("./MoreButton"));

type PublicationSnippetProps = {
  publication: any;
  isCompact?: boolean;
  useModal?: boolean;
  useIframe?: boolean;
  originEntity?: any;
  originCollection?: any;
  originWatchlist?: any;
  origin?: {
    endpoint: string;
    filters?: { [key: string]: string };
    sort?: {
      id: string;
      sortStr: string;
    };
    page?: number;
    pageSize?: number;
  };
  moreOptions?: MoreOptions;
  swr?: any;
  style?: "snippet" | "card" | "snippetV2" | "keyInsight";
  query?: string;
  noBorder?: boolean;
  clickAnywhere?: boolean;
  hideActions?: boolean;
  hideFollowCTA?: boolean;
  hideAuthor?: boolean;
  summaryClassName?: string;
  containerClassName?: string;
};

export default function PublicationSnippet({
  publication,
  isCompact,
  useModal,
  useIframe,
  originEntity,
  originCollection,
  originWatchlist,
  origin,
  moreOptions,
  swr,
  style = "snippet",
  query,
  noBorder,
  clickAnywhere,
  hideActions,
  hideAuthor,
  summaryClassName,
  hideFollowCTA,
  containerClassName,
}: PublicationSnippetProps) {
  const router = useRouter();
  const analysisType = router.query.tab as string;

  const {
    authenticated,
    account: loggedInAccount,
    managedEntity,
  } = useAuthentication();
  const { mobile } = useMediaQuery();
  const theme = useTheme();
  const [_, setShowInsightModalGlobal] = useGlobalState("insightModal", null);
  const [showInsightModal, setShowInsightModal] = useState<any>(null);
  const toggleInsightModal = useCallback(() => {
    if (showInsightModal) {
      setShowInsightModal(null);
      setShowInsightModalGlobal(null);
    } else {
      setShowInsightModal(publication?.asset || publication);
      setShowInsightModalGlobal(publication?.asset || publication);
    }
  }, [publication, setShowInsightModalGlobal, showInsightModal]);

  const {
    id,
    slug,
    type,
    tagline,
    company,
    contentVertical,
    totalDiscussions,
    byline,
    boomerangReason,
    imperative,
    publishedAt,
    republishedAt,
    totalCombinedRead,
    latestCommentThread: latestCommentThreadSrc,
    executiveSummary,
    authorTitle,
    publicationType,
    coverImageUrl,
    searchHighlight,
  } = publication?.asset || publication;

  let entity = (publication?.asset || publication).entity;
  if (publication.type === "external-reports") {
    entity = (publication?.asset || publication).primaryEntity;
  }

  const { isLiked, like, shareOptions } = useInsightInteractions({
    insight: publication?.asset || publication,
    lockerItem: publication?.asset ? publication : null,
    source: "snippet",
  });

  const { data: phoneNumber, isValidating: isPhoneNumberValidating } = useApi(
    loggedInAccount?.id
      ? `/api/v2/accounts/${loggedInAccount?.id}/phone-number`
      : null,
    baseFetcher
  );

  const [latestCommentThread, setLatestCommentThread] = useState<any>(
    latestCommentThreadSrc
  );
  const [account, setAccount] = useState<any>(publication?.account);

  const [showComments, setShowComments] = useState("");

  // TODO: continue revamp
  useEffect(() => {
    if (!publication.asset?.id) {
      return;
    }

    (async () => {
      try {
        const latestComment = await fetchApi(
          `/api/v2/insights/${publication.asset?.id}/latest-comment-thread?include=attachments`
        );

        const account = await fetchApi(
          `/api/v2/insights/${publication.asset?.id}/account`
        );

        let commenter = null;

        if (latestComment?.id) {
          commenter = await fetchApi(
            `/api/v2/comments/${latestComment.id}/commenter`
          );

          if (!commenter.id) {
            commenter = null;
          }
        }

        latestComment.commenter = commenter;
        setLatestCommentThread(latestComment);

        setAccount(account);
      } catch (error: any) {
        Bugsnag.notify(error);
      }
    })();
  }, [publication?.id, publication?.asset]);

  const isCompany = company?.publishUnderCompanyName;
  const author = isCompany ? company : account;

  let summary = "";

  summary =
    searchHighlight ||
    boomerangReason ||
    byline ||
    htmlText(executiveSummary) ||
    "";
  summary = unescape(summary);
  summary = truncate(summary, {
    length: 150,
    separator: /[/\- ]/,
  });

  const authorURL = `/${isCompany ? "companies" : "profiles"}/${author?.slug}`;

  const { showModal } = useIframeModal();

  const onClick = useCallback(
    (event: any) => {
      if (useModal) {
        event.preventDefault();
        event.stopPropagation();
        if (mobile) {
          try {
            sessionStorage.setItem("entity-slug", originEntity?.slug);
            sessionStorage.setItem("collection-slug", originCollection?.slug);
            sessionStorage.setItem("analysis-type", analysisType);
          } catch (error: any) {
            Bugsnag.notify(error);
          }
          const link = `${
            publication.type === "insights" ? `/insights` : `/reports`
          }/${publication.slug}`;
          router.push(link);
        } else if (
          USE_EMBER_IFRAME &&
          (useIframe || !loggedInAccount?.isInternalUser)
        ) {
          const link = `${
            publication.type === "insights" ? `/insights` : `/reports`
          }/${publication.slug}`;
          window.history.pushState(link, "", link);

          const query: { [key: string]: string | string[] } = {};
          if (origin) {
            query["origin-endpoint"] = origin.endpoint;
            if (origin.page) query["origin-page"] = origin.page.toString();
            if (origin.pageSize)
              query["origin-page-size"] = origin.pageSize.toString();
            if (origin.sort) query["origin-sort"] = origin.sort.id;
            if (origin.filters) {
              query["origin-filters[]"] = Object.entries(origin.filters).map(
                ([key, value]) => encodeURIComponent(`${key}=${value}`)
              );
            }
          }
          let queryString = objectToQueryString(query);

          showModal(
            true,
            `/embed/insights/${slug}${queryString}${
              mobile ? "#zoom=1" : "#zoom=FitW"
            }`,
            () => {
              if (router.query.slug || router.pathname === "/dashboard") {
                router.push(
                  {
                    pathname: router.pathname,
                    query: router.query,
                  },
                  undefined,
                  { shallow: true }
                );
              } else {
                router.back();
              }
            }
          );
        } else {
          const link = `${
            type === "insights" ? `/insights` : `/reports`
          }/${slug}`;
          window.history.pushState(link, "", link);

          toggleInsightModal();
        }
      }
    },
    [
      useModal,
      mobile,
      useIframe,
      loggedInAccount?.isInternalUser,
      originEntity?.slug,
      originCollection?.slug,
      analysisType,
      publication.type,
      publication.slug,
      origin,
      showModal,
      slug,
      router,
      type,
      toggleInsightModal,
    ]
  );

  const link = `${
    publication.type === "insights" ? "/insights" : `${emberAppBaseUrl}/reports`
  }/${publication.slug}`;

  const views = numeral(totalCombinedRead).format("0.[0]a");

  const imperativeText = useMemo(() => {
    if (theme === "bos") {
      if (imperative === "neutral") return "neutral";

      return imperative === "bullish" ? "buy" : "sell";
    }

    return imperative;
  }, [imperative, theme]);

  const shouldShowSnippet = useMemo(() => {
    if (loggedInAccount?.isPreviewPass) {
      return !(
        !isPhoneNumberValidating &&
        !(phoneNumber?.verified || phoneNumber?.skipVerification)
      );
    }

    return (
      !mobile &&
      !isCompact &&
      !loggedInAccount?.isIndividualPass &&
      !loggedInAccount?.isCorporateOfficer &&
      totalDiscussions > 0 &&
      !showComments
    );
  }, [
    isCompact,
    isPhoneNumberValidating,
    loggedInAccount?.isCorporateOfficer,
    loggedInAccount?.isIndividualPass,
    loggedInAccount?.isPreviewPass,
    mobile,
    phoneNumber?.skipVerification,
    phoneNumber?.verified,
    showComments,
    totalDiscussions,
  ]);

  if (style === "card") {
    const imageUrl = author.isCompany
      ? author?.avatarUrl?.xlarge
      : coverImageUrl || author?.avatarUrl?.xlarge;

    return (
      <div
        className={twMerge(
          "border border-solid border-inactive-gray",
          "rounded-[5px]",
          "min-h-[245px]",
          "max-w-[200px]",
          "group"
        )}
      >
        <div className="overflow-hidden">
          <Link href={link}>
            <div
              style={{ backgroundImage: `url(${imageUrl})` }}
              className={twMerge(
                "rouned-t-[5px]",
                "w-[198px] h-[118px]",
                "bg-center bg-cover",
                "group-hover:scale-125 transition-transform ease-in-out"
              )}
            />
          </Link>
        </div>
        <div
          className={twMerge(
            "border-0 border-t border-solid border-inactive-gray",
            "p-[10px]"
          )}
        >
          <div className="line-clamp-3 min-h-[68px] mb-[10px]">
            <Link
              href={link}
              className="text-[13px] font-bold leading-[18px] break-words"
            >
              {query ? highlightText(tagline, query) : tagline}
            </Link>
          </div>
          <div className="flex space-x-[7px] text-[11px]">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <div className="text-left">
                    {author?.name}
                    <br />
                    {authorTitle}
                  </div>
                </Tooltip>
              }
            >
              <div>
                <Link href={authorURL} onClick={(e) => e.stopPropagation()}>
                  <ProfileLogo
                    isSquare={isCompany}
                    profileURL={authorURL}
                    logoSrc={author?.avatarUrl?.small}
                    size={"compact"}
                  />
                </Link>
              </div>
            </OverlayTrigger>
            <div className="flex-1 max-w-[100px]">
              <Link
                href={authorURL}
                className="block truncate"
                onClick={(e) => e.stopPropagation()}
              >
                {author?.name}
              </Link>
              {publishedAt && (
                <div suppressHydrationWarning>
                  {format(
                    parseJSON(republishedAt || publishedAt),
                    "dd MMM yyyy HH:mm"
                  )}
                </div>
              )}
            </div>
            <div>
              {authenticated && (
                <PublicationSaveButton
                  publication={publication}
                  iconOnly
                  className="text-[19px]"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (style === "keyInsight") {
    return (
      <>
        <div
          id={`publication-snippet-${publication.slug}`}
          className={classNames(
            styles.publicationSnippetContainer,
            mobile && styles.mobileLayout,
            "group",
            noBorder && styles.noBorder,
            clickAnywhere &&
              "!p-[15px] cursor-pointer md:hover:bg-nearly-white",
            "flex space-x-[30px]",
            containerClassName
          )}
          onClick={(e) => clickAnywhere && onClick(e)}
        >
          <div className="flex-1">
            <div className={styles.publicationInfo}>
              <div className="space-x-[7px] flex w-full max-w-xs md:max-w-none md:w-auto">
                <div className="space-x-[7px] truncate">
                  {imperativeText && (
                    <span
                      className={`${styles.imperative} ${styles[imperative]}`}
                    >
                      {imperativeText}
                    </span>
                  )}
                  {entity ? (
                    <>
                      {imperativeText && <span>•</span>}
                      <span className={twMerge(styles.entityName, "truncate")}>
                        <Link
                          href={`/entities/${entity.slug}`}
                          prefetch={false}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {entity.prettyName?.trim()}
                        </Link>
                      </span>
                    </>
                  ) : contentVertical ? (
                    <>
                      <span>•</span>
                      <span className={twMerge(styles.entityName, "truncate")}>
                        <Link
                          href={`${emberAppBaseUrl}/verticals/${contentVertical.slug}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {contentVertical.name}
                        </Link>
                      </span>
                    </>
                  ) : (
                    <></>
                  )}
                </div>
                {publishedAt && (
                  <>
                    {(imperativeText || entity || contentVertical) && (
                      <span>•</span>
                    )}
                    <span
                      className={styles.publicationTime}
                      suppressHydrationWarning
                    >
                      {format(
                        parseJSON(republishedAt || publishedAt),
                        "dd MMM yyyy HH:mm"
                      )}
                    </span>
                  </>
                )}
                {publicationType && (
                  <>
                    <span>•</span>
                    <span>{publicationType}</span>
                  </>
                )}
              </div>
              {moreOptions && (
                <MoreButton publication={publication} {...moreOptions} />
              )}
            </div>
            <PublicationBadges publication={publication} />
            <div className={styles.publicationContent}>
              <Link href={link} prefetch={false} onClick={onClick}>
                <h3
                  className={classNames(
                    styles.header,
                    publication.isRead && styles.read,
                    "mb-0 break-words",
                    "!text-[15px] line-clamp-1"
                  )}
                >
                  {publication.isEmbargoed && (
                    <FontAwesomeIcon icon={faLock} className={styles.icon} />
                  )}
                  {publication.conviction === 1 && (
                    <FontAwesomeIcon
                      icon={faStar}
                      className={classNames(styles.icon, styles.topPick)}
                    />
                  )}
                  {publication.republishedAt && (
                    <OverlayTrigger
                      overlay={
                        <Tooltip>
                          {account.shortName} has resurfaced this previously
                          published Insight
                        </Tooltip>
                      }
                    >
                      <span>
                        <FontAwesomeIcon
                          icon={faSync}
                          className={classNames(styles.icon, styles.topPick)}
                        />
                      </span>
                    </OverlayTrigger>
                  )}
                  {query ? highlightText(tagline, query) : tagline}
                </h3>
              </Link>
              {!isCompact && (
                <h4
                  className={twMerge(
                    styles.summary,
                    "font-normal",
                    summaryClassName
                  )}
                >
                  {query ? highlightText(summary, query) : summary}
                </h4>
              )}
            </div>
          </div>
          <div
            className={twMerge(
              styles.snippetBottomContainer,
              "min-w-[200px] !mt-0"
            )}
          >
            {!hideAuthor && (
              <div className={classNames(styles.authorInfo, "relative")}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <div className="text-left">
                        {author?.name}
                        <br />
                        {authorTitle}
                      </div>
                    </Tooltip>
                  }
                >
                  <div className="flex items-center justify-center mr-[7px]">
                    <ProfileLogo
                      isSquare={isCompany}
                      profileURL={authorURL}
                      logoSrc={author?.avatarUrl?.small}
                      size={"compact"}
                    />
                  </div>
                </OverlayTrigger>
                <div className="flex items-center">
                  <Link
                    href={authorURL}
                    className={styles.authorName}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h5 className="text-[12px] font-bold mb-0 leading-[20px] truncate">
                      {author?.name}
                    </h5>
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  if (style === "snippetV2") {
    return (
      <>
        <div
          id={`publication-snippet-${publication.slug}`}
          className={classNames(
            styles.publicationSnippetContainer,
            mobile && styles.mobileLayout,
            "group",
            noBorder && styles.noBorder,
            clickAnywhere &&
              "!p-[15px] cursor-pointer md:hover:bg-nearly-white",
            containerClassName
          )}
          onClick={(e) => clickAnywhere && onClick(e)}
        >
          <div className={styles.publicationInfo}>
            <div className="space-x-[7px] flex w-full max-w-xs md:max-w-none md:w-auto">
              <div className="space-x-[7px] truncate">
                {imperativeText && (
                  <span
                    className={`${styles.imperative} ${styles[imperative]}`}
                  >
                    {imperativeText}
                  </span>
                )}
                {entity ? (
                  <>
                    {imperativeText && <span>•</span>}
                    <span className={twMerge(styles.entityName, "truncate")}>
                      <Link
                        href={`/entities/${entity.slug}`}
                        prefetch={false}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {entity.prettyName?.trim()}
                      </Link>
                    </span>
                  </>
                ) : contentVertical ? (
                  <>
                    <span>•</span>
                    <span className={twMerge(styles.entityName, "truncate")}>
                      <Link
                        href={`${emberAppBaseUrl}/verticals/${contentVertical.slug}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {contentVertical.name}
                      </Link>
                    </span>
                  </>
                ) : (
                  <></>
                )}
              </div>
              {publishedAt && (
                <>
                  {(imperativeText || entity || contentVertical) && (
                    <span>•</span>
                  )}
                  <span
                    className={styles.publicationTime}
                    suppressHydrationWarning
                  >
                    {format(
                      parseJSON(republishedAt || publishedAt),
                      "dd MMM yyyy HH:mm"
                    )}
                  </span>
                </>
              )}
              {publicationType && (
                <>
                  <span>•</span>
                  <span>{publicationType}</span>
                </>
              )}
            </div>
            {moreOptions && (
              <MoreButton publication={publication} {...moreOptions} />
            )}
          </div>
          <PublicationBadges publication={publication} />
          <div className={styles.publicationContent}>
            <Link href={link} prefetch={false} onClick={onClick}>
              <h3
                className={classNames(
                  styles.header,
                  publication.isRead && styles.read,
                  "mb-0 break-words"
                )}
              >
                {publication.isEmbargoed && (
                  <FontAwesomeIcon icon={faLock} className={styles.icon} />
                )}
                {publication.conviction === 1 && (
                  <FontAwesomeIcon
                    icon={faStar}
                    className={classNames(styles.icon, styles.topPick)}
                  />
                )}
                {publication.republishedAt && (
                  <OverlayTrigger
                    overlay={
                      <Tooltip>
                        {account.shortName} has resurfaced this previously
                        published Insight
                      </Tooltip>
                    }
                  >
                    <span>
                      <FontAwesomeIcon
                        icon={faSync}
                        className={classNames(styles.icon, styles.topPick)}
                      />
                    </span>
                  </OverlayTrigger>
                )}
                {query ? highlightText(tagline, query) : tagline}
              </h3>
            </Link>
            {!isCompact && (
              <h4
                className={twMerge(
                  styles.summary,
                  "font-normal",
                  summaryClassName
                )}
              >
                {query ? highlightText(summary, query) : summary}
              </h4>
            )}
          </div>
          <div className={styles.snippetBottomContainer}>
            {!hideAuthor && (
              <div className={classNames(styles.authorInfo, "relative")}>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      <div className="text-left">
                        {author?.name}
                        <br />
                        {authorTitle}
                      </div>
                    </Tooltip>
                  }
                >
                  <div className="flex items-center justify-center mr-[7px]">
                    <ProfileLogo
                      isSquare={isCompany}
                      profileURL={authorURL}
                      logoSrc={author?.avatarUrl?.small}
                      size={"compact"}
                    />
                  </div>
                </OverlayTrigger>
                <div>
                  <div className="flex items-center">
                    <Link
                      href={authorURL}
                      className={styles.authorName}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h5 className="text-[12px] font-bold mb-0 leading-[20px] truncate">
                        {author?.name}
                      </h5>
                    </Link>
                    {!hideFollowCTA && author?.id !== loggedInAccount?.id && (
                      <FollowButton
                        company={company}
                        profile={author}
                        style="text"
                        hideOnActive
                      />
                    )}
                  </div>

                  <div className="flex space-x-[15px]">
                    <div className={styles.views}>{views} Views</div>
                    {loggedInAccount?.canViewComment &&
                      totalDiscussions > 0 && (
                        <div
                          className={twMerge(styles.views, "cursor-pointer")}
                          onClick={() =>
                            setShowComments(showComments ? "" : "show")
                          }
                        >
                          {totalDiscussions} discussion
                          {totalDiscussions > 1 ? "s" : ""}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}
            {!hideActions && (
              <div
                className={classNames(
                  styles.publicationActions,
                  isCompact && !mobile && styles.compact,
                  "space-x-[20px] flex !text-base md:text-sm"
                )}
              >
                {authenticated &&
                  loggedInAccount?.canPostComment &&
                  !loggedInAccount?.isCorporateOfficer && (
                    <div
                      className={classNames(
                        "cursor-pointer flex items-center justify-center",
                        "text-intelligent-silver hover:text-active-black"
                      )}
                      onClick={() =>
                        setShowComments(showComments ? "" : "post")
                      }
                    >
                      <FontAwesomeIcon
                        icon={faCommentDots}
                        className="mr-[5px]"
                      />
                      {mobile ? "" : "Post"}
                    </div>
                  )}
                {authenticated && loggedInAccount?.id !== account?.id && (
                  <div
                    className={classNames(
                      "cursor-pointer flex items-center justify-center",
                      "text-intelligent-silver hover:text-active-black"
                    )}
                    onClick={like}
                  >
                    <FontAwesomeIcon
                      icon={isLiked ? faThumbsUpSolid : faThumbsUp}
                      className="mr-[5px]"
                    />
                    {mobile ? "" : isLiked ? "Liked" : "Like"}
                  </div>
                )}
                <ShareButton
                  className="flex items-center justify-center text-intelligent-silver"
                  options={shareOptions}
                  columnView
                  noBorder
                  profile={author?.isCompany ? undefined : author}
                  noTooltip
                >
                  {mobile ? "" : "Share"}
                </ShareButton>
                {authenticated && (
                  <PublicationSaveButton
                    publication={publication}
                    className="flex items-center justify-center"
                  />
                )}
              </div>
            )}
          </div>
          {shouldShowSnippet && (
            <>
              <div className="pl-[40px] mt-[15px]">
                <DiscussionSnippet
                  discussion={latestCommentThread}
                  noBorder
                  hideSource
                  hideActions
                  hideReply
                  hideLatestReply
                  truncateBody={300}
                  customBodyClassName={twMerge(
                    "hover:bg-[#eee]",
                    "w-full",
                    "cursor-pointer"
                  )}
                  style="compactV2"
                  openModal
                />
              </div>
              {loggedInAccount?.canViewComment && totalDiscussions > 1 && (
                <div
                  className="text-[14px] text-smart-teal text-center cursor-pointer mt-[-10px]"
                  onClick={() => setShowComments("show")}
                >
                  View More Discussion Threads
                </div>
              )}
            </>
          )}
          {showComments && (
            <div className="mt-[15px] py-[15px] pl-[40px]">
              <InsightDiscussionsList
                slug={slug}
                className="min-h-0"
                customBodyClassName={twMerge("hover:bg-[#eee]", "w-full")}
                snippetStyle="v2"
                composerOnTop
                showComposerByDefault={showComments === "post"}
                miniComposer
              />
              <div
                className={twMerge(
                  "text-[14px] text-smart-teal text-center cursor-pointer",
                  totalDiscussions > 0 ? "mt-[-10px]" : "mt-[15px]"
                )}
                onClick={() => setShowComments("")}
              >
                Hide Discussion Threads
              </div>
            </div>
          )}
        </div>
        {showInsightModal?.id === id && (
          <InsightModal
            slug={showInsightModal?.slug}
            insight={{ ...showInsightModal, account }}
            isOpen={!!showInsightModal}
            swr={swr}
            onClose={() => {
              toggleInsightModal();
              if (router.query.slug || router.pathname === "/dashboard") {
                const url = router.asPath;
                window.history.pushState(url, "", url);
              } else {
                router.back();
              }
            }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        id={`publication-snippet-${publication.slug}`}
        className={classNames(
          styles.publicationSnippetContainer,
          mobile && styles.mobileLayout,
          "group",
          noBorder && styles.noBorder,
          clickAnywhere && "!p-[15px] cursor-pointer md:hover:bg-nearly-white",
          containerClassName
        )}
        onClick={(e) => clickAnywhere && onClick(e)}
      >
        <div className={styles.publicationInfo}>
          <div className="space-x-[7px] flex w-full max-w-xs md:max-w-none md:w-auto">
            <div className="space-x-[7px] truncate">
              {imperativeText && (
                <span className={`${styles.imperative} ${styles[imperative]}`}>
                  {imperativeText}
                </span>
              )}
              {entity ? (
                <>
                  {imperativeText && <span>•</span>}
                  <span className={twMerge(styles.entityName, "truncate")}>
                    <Link
                      href={`/entities/${entity.slug}`}
                      prefetch={false}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {entity.prettyName?.trim()}
                    </Link>
                  </span>
                </>
              ) : contentVertical ? (
                <>
                  <span>•</span>
                  <span className={twMerge(styles.entityName, "truncate")}>
                    <Link
                      href={`${emberAppBaseUrl}/verticals/${contentVertical.slug}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {contentVertical.name}
                    </Link>
                  </span>
                </>
              ) : (
                <></>
              )}
            </div>
            {publishedAt && (
              <>
                {(imperativeText || entity || contentVertical) && (
                  <span>•</span>
                )}
                <span
                  className={styles.publicationTime}
                  suppressHydrationWarning
                >
                  {format(
                    parseJSON(republishedAt || publishedAt),
                    "dd MMM yyyy HH:mm"
                  )}
                </span>
              </>
            )}
            {publicationType && (
              <>
                <span>•</span>
                <span>{publicationType}</span>
              </>
            )}
          </div>
          {moreOptions && (
            <MoreButton publication={publication} {...moreOptions} />
          )}
        </div>
        <PublicationBadges publication={publication} />
        <div className={styles.publicationContent}>
          <Link href={link} prefetch={false} onClick={onClick}>
            <h3
              className={classNames(
                styles.header,
                publication.isRead && styles.read,
                "mb-0 break-words"
              )}
            >
              {publication.isEmbargoed && (
                <FontAwesomeIcon icon={faLock} className={styles.icon} />
              )}
              {publication.conviction === 1 && (
                <FontAwesomeIcon
                  icon={faStar}
                  className={classNames(styles.icon, styles.topPick)}
                />
              )}
              {publication.republishedAt && (
                <OverlayTrigger
                  overlay={
                    <Tooltip>
                      {account.shortName} has resurfaced this previously
                      published Insight
                    </Tooltip>
                  }
                >
                  <span>
                    <FontAwesomeIcon
                      icon={faSync}
                      className={classNames(styles.icon, styles.topPick)}
                    />
                  </span>
                </OverlayTrigger>
              )}
              {query ? highlightText(tagline, query) : tagline}
            </h3>
          </Link>
          {!isCompact && (
            <h4
              className={twMerge(
                styles.summary,
                "font-normal",
                summaryClassName
              )}
            >
              {query ? highlightText(summary, query) : summary}
            </h4>
          )}
        </div>
        <div className={styles.snippetBottomContainer}>
          {!hideAuthor && (
            <div className={classNames(styles.authorInfo, "relative")}>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip>
                    <div className="text-left">
                      {author?.name}
                      <br />
                      {authorTitle}
                    </div>
                  </Tooltip>
                }
              >
                <div className="flex items-center justify-center mr-[7px]">
                  <ProfileLogo
                    isSquare={isCompany}
                    profileURL={authorURL}
                    logoSrc={author?.avatarUrl?.small}
                    size={"compact"}
                  />
                </div>
              </OverlayTrigger>
              <div>
                <div className="flex items-center">
                  <Link
                    href={authorURL}
                    className={styles.authorName}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <h5 className="text-[12px] font-bold mb-0 leading-[20px] truncate">
                      {author?.name}
                    </h5>
                  </Link>
                  {!hideFollowCTA && author?.id !== loggedInAccount?.id && (
                    <FollowButton
                      company={company}
                      profile={author}
                      style="text"
                      hideOnActive
                    />
                  )}
                </div>
                <div className={styles.views}>{views} Views</div>
              </div>
            </div>
          )}
          {!hideActions && (
            <div
              className={classNames(
                styles.publicationActions,
                isCompact && !mobile && styles.compact,
                "space-x-[20px] flex !text-base md:text-sm"
              )}
            >
              {authenticated && loggedInAccount?.id !== account?.id && (
                <div
                  className={classNames(
                    "cursor-pointer flex items-center justify-center",
                    "text-intelligent-silver hover:text-active-black"
                  )}
                  onClick={like}
                >
                  <FontAwesomeIcon
                    icon={isLiked ? faThumbsUpSolid : faThumbsUp}
                    className="mr-[5px]"
                  />
                  {mobile ? "" : isLiked ? "Liked" : "Like"}
                </div>
              )}
              <ShareButton
                className="flex items-center justify-center text-intelligent-silver"
                options={shareOptions}
                columnView
                noBorder
                profile={author?.isCompany ? undefined : author}
                noTooltip
              >
                {mobile ? "" : "Share"}
              </ShareButton>
              {authenticated && (
                <PublicationSaveButton
                  publication={publication}
                  className="flex items-center justify-center"
                />
              )}
            </div>
          )}
        </div>
        {!mobile &&
          !isCompact &&
          !loggedInAccount?.isIndividualPass &&
          totalDiscussions > 0 && (
            <>
              <div className="mt-[10px] text-[13px] text-active-black">
                {totalDiscussions} discussion
                {totalDiscussions > 1 ? "s" : ""}
              </div>
              <div>
                <DiscussionSnippet
                  discussion={latestCommentThread}
                  noBorder
                  hideSource
                  hideActions
                  truncateBody={300}
                  customBodyClassName={twMerge(
                    "bg-[#f5f5f5] hover:bg-[#eee]",
                    "p-[10px] rounded-[10px] w-full",
                    "cursor-pointer"
                  )}
                />
              </div>
            </>
          )}
      </div>
      {showInsightModal?.id === id && (
        <InsightModal
          slug={showInsightModal?.slug}
          insight={{ ...showInsightModal, account }}
          isOpen={!!showInsightModal}
          swr={swr}
          onClose={() => {
            toggleInsightModal();
            if (router.query.slug || router.pathname === "/dashboard") {
              const url = router.asPath;
              window.history.pushState(url, "", url);
            } else {
              router.back();
            }
          }}
        />
      )}
    </>
  );
}
